import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Button from "./Button";
import { api, IAddress, ICreateAddressRequest, getNullableString } from "../api/api";

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: IAddress) => void;
  selectedAddressId?: number;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: #081c21;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #8ca9a9;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Header = styled.div`
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #8ca9a9;
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 4px;
  margin-top: 16px;
`;

const Tab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 12px 16px;
  background-color: ${(props) => (props.$isActive ? "#24b97d" : "transparent")};
  color: ${(props) => (props.$isActive ? "#ffffff" : "#8ca9a9")};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    background-color: ${(props) =>
      props.$isActive ? "#24b97d" : "rgba(255, 255, 255, 0.05)"};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;

  /* Кастомный скроллбар */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddressCard = styled.div<{ $isSelected: boolean }>`
  padding: 16px;
  background-color: ${(props) =>
    props.$isSelected
      ? "rgba(36, 185, 125, 0.1)"
      : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid
    ${(props) => (props.$isSelected ? "#24b97d" : "rgba(255, 255, 255, 0.1)")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected
        ? "rgba(36, 185, 125, 0.15)"
        : "rgba(255, 255, 255, 0.05)"};
    border-color: ${(props) =>
      props.$isSelected ? "#24b97d" : "rgba(255, 255, 255, 0.2)"};
  }
`;

const AddressText = styled.div`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
  margin-bottom: 4px;
`;

const AddressDetails = styled.div`
  font-size: 12px;
  color: #8ca9a9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddressActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #8ca9a9;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.edit:hover {
    color: #24b97d;
  }

  &.delete:hover {
    color: #ff6b6b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8ca9a9;
  font-size: 14px;
`;

const NewAddressForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: #8ca9a9;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: #8ca9a9;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ffffff;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #24b97d;
  }
`;

const Footer = styled.div`
  padding: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  onSelectAddress,
  selectedAddressId,
}) => {
  const [activeTab, setActiveTab] = useState<"list" | "new">("list");
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingAddress, setEditingAddress] = useState<IAddress | null>(null);
  const [formData, setFormData] = useState<ICreateAddressRequest>({
    address: "",
    first_name: "",
    last_name: "",
    middle_name: "",
  });

  useEffect(() => {
    if (isOpen) {
      loadAddresses();
    }
  }, [isOpen]);

  const loadAddresses = async () => {
    setIsLoading(true);
    try {
      const addressList = await api.getAddresses();
      setAddresses(addressList);
    } catch (error) {
      console.error("Ошибка загрузки адресов:", error);
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof ICreateAddressRequest,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveAddress = async () => {
    if (!formData.address || !formData.first_name || !formData.last_name) {
      return;
    }

    setIsLoading(true);
    try {
      let savedAddress: IAddress;
      if (editingAddress) {
        savedAddress = await api.updateAddress(editingAddress.Id, formData);
      } else {
        savedAddress = await api.createAddress(formData);
      }

      await loadAddresses();
      setActiveTab("list");
      setEditingAddress(null);
      resetForm();
    } catch (error) {
      console.error("Ошибка сохранения адреса:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAddress = (address: IAddress) => {
    setFormData({
      address: address.address_name,
      first_name: getNullableString(address.first_name),
      last_name: getNullableString(address.last_name),
      middle_name: getNullableString(address.middle_name),
    });
    setEditingAddress(address);
    setActiveTab("new");
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (confirm("Вы уверены, что хотите удалить этот адрес?")) {
      setIsLoading(true);
      try {
        await api.deleteAddress(addressId);
        await loadAddresses();
      } catch (error) {
        console.error("Ошибка удаления адреса:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      address: "",
      first_name: "",
      last_name: "",
      middle_name: "",
    });
    setEditingAddress(null);
  };

  const handleSelectAndClose = (address: IAddress) => {
    onSelectAddress(address);
    onClose();
  };

  const formatAddress = (address: IAddress) => {
    return address.address_name;
  };

  const isFormValid = () => {
    return (
      formData.address.trim() &&
      formData.first_name.trim() &&
      formData.last_name.trim()
    );
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </CloseButton>

        <Header>
          <Title>Управление адресами</Title>
          <Subtitle>Выберите существующий адрес или добавьте новый</Subtitle>
          <TabsContainer>
            <Tab
              $isActive={activeTab === "list"}
              onClick={() => {
                setActiveTab("list");
                resetForm();
              }}
            >
              Мои адреса
            </Tab>
            <Tab
              $isActive={activeTab === "new"}
              onClick={() => setActiveTab("new")}
            >
              {editingAddress ? "Редактировать" : "Добавить новый"}
            </Tab>
          </TabsContainer>
        </Header>

        <Content>
          {activeTab === "list" ? (
            <AddressList>
              {isLoading ? (
                <EmptyState>Загрузка адресов...</EmptyState>
              ) : addresses.length === 0 ? (
                <EmptyState>
                  У вас пока нет сохраненных адресов.
                  <br />
                  Добавьте новый адрес для быстрого оформления заказов.
                </EmptyState>
              ) : (
                addresses.map((address) => (
                  <AddressCard
                    key={address.Id}
                    $isSelected={address.Id === selectedAddressId}
                    onClick={() => handleSelectAndClose(address)}
                  >
                    <AddressText>{formatAddress(address)}</AddressText>
                    <AddressDetails>
                      <span>
                        {getNullableString(address.first_name)} {getNullableString(address.last_name)}{" "}
                        {getNullableString(address.middle_name)}
                      </span>
                      <AddressActions>
                        <ActionButton
                          className="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAddress(address);
                          }}
                        >
                          Изменить
                        </ActionButton>
                        <ActionButton
                          className="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAddress(address.Id);
                          }}
                        >
                          Удалить
                        </ActionButton>
                      </AddressActions>
                    </AddressDetails>
                  </AddressCard>
                ))
              )}
            </AddressList>
          ) : (
            <NewAddressForm>
              <FormRow>
                <FormGroup>
                  <Label>Фамилия *</Label>
                  <Input
                    type="text"
                    placeholder="Иванов"
                    value={formData.last_name}
                    onChange={(e) =>
                      handleInputChange("last_name", e.target.value)
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Имя *</Label>
                  <Input
                    type="text"
                    placeholder="Иван"
                    value={formData.first_name}
                    onChange={(e) =>
                      handleInputChange("first_name", e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Отчество</Label>
                <Input
                  type="text"
                  placeholder="Иванович"
                  value={formData.middle_name}
                  onChange={(e) =>
                    handleInputChange("middle_name", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Адрес доставки *</Label>
                <Input
                  type="text"
                  placeholder="г. Москва, ул. Тверская, д. 1, кв. 10"
                  value={formData.address}
                  onChange={(e) =>
                    handleInputChange("address", e.target.value)
                  }
                />
              </FormGroup>
            </NewAddressForm>
          )}
        </Content>

        <Footer>
          <div />
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            {activeTab === "new" && (
              <Button
                variant="primary"
                onClick={handleSaveAddress}
                disabled={!isFormValid() || isLoading}
              >
                {isLoading
                  ? "Сохранение..."
                  : editingAddress
                  ? "Обновить"
                  : "Сохранить"}
              </Button>
            )}
          </ButtonGroup>
        </Footer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddressModal;
